/**
 * Lazy loader for the search core. The UI shells (desktop-search.js,
 * mobile-search-drawer.js) load eagerly and open instantly on interaction —
 * the server-rendered dropdown/drawer content (skeletons when SearchSpring is
 * active, real product cards otherwise) covers the gap. The heavy part —
 * `@stardust/search` and its product-card-builder / swatch-hydrator graph,
 * plus any provider wiring registered via onSearchLoad() — is deferred here
 * until a shell first opens.
 *
 * Also syncs `?q=` from the URL into every search input on page load — this
 * used to be desktop-search.js's and mobile-search-drawer.js's job, but it's
 * centralized here (this script loads first) so a late-initializing module
 * can't clobber text the user already typed.
 */

const SEARCH_INPUT_SELECTOR = '[data-search-input]';
const MOBILE_SEARCH_INPUT_SELECTOR = '[data-mobile-search-input]';
const MOBILE_SEARCH_TRIGGER_TEXT_SELECTOR = '[data-mobile-search-trigger-text]';

// Persist search term from URL (e.g., /collections/shop?q=bunk+beds) into every
// search input before anything else runs.
// Skip type=article — that's a blog search (blog-hero.liquid owns that term).
const urlParams = new URLSearchParams(window.location.search);
const urlQuery = urlParams.get('q');
if (urlQuery && urlParams.get('type') !== 'article') {
  document.querySelectorAll(`${SEARCH_INPUT_SELECTOR}, ${MOBILE_SEARCH_INPUT_SELECTOR}`).forEach((input) => {
    input.value = urlQuery;
  });
  // The mobile trigger (shown in place of the drawer until opened) is a
  // placeholder span, not an input — mirror the query there too.
  document.querySelectorAll(MOBILE_SEARCH_TRIGGER_TEXT_SELECTOR).forEach((el) => {
    el.textContent = urlQuery;
  });
}

let corePromise = null;

/** Deferred wiring registered via onSearchLoad(), drained on first load. */
const lazyTasks = [];

/**
 * Register extra work to run alongside the first search-core import — e.g.
 * scripts.liquid's SearchSpring provider wiring, which would otherwise eagerly
 * pull `@stardust/search` (and its API calls) on every page. The returned
 * promise is awaited before loadSearchCore() resolves, so wiring that must
 * precede the first search (setProvider) is guaranteed in place — but only
 * if registration happens before anything can call loadSearchCore(): keep
 * registrations ahead of the UI shell <script> tags in scripts.liquid. If
 * loading has already started, the task still runs immediately, but callers
 * already holding the core promise won't wait for it.
 * @param {() => Promise<any>} task
 */
export function onSearchLoad(task) {
  if (corePromise) task();
  else lazyTasks.push(task);
}

/**
 * Import the search core (BaseSearch + registered provider wiring), memoized.
 * @returns {Promise<typeof import('@stardust/search').default | null>}
 *   The BaseSearch class, or null if the import failed (the memo is reset so
 *   the next call retries instead of leaving search dead for the session).
 */
export function loadSearchCore() {
  if (!corePromise) {
    // Snapshot, don't drain yet: only clear lazyTasks once this attempt
    // succeeds. A failed attempt leaves the tasks queued so a retry re-runs
    // the provider wiring instead of leaving it permanently unset.
    const tasks = lazyTasks.slice();
    corePromise = Promise.all([import('@stardust/search'), ...tasks.map((task) => task())])
      .then(([searchModule]) => {
        lazyTasks.length = 0;
        return searchModule.default;
      })
      .catch((err) => {
        console.error('[SearchLoader] import failed:', err);
        corePromise = null;
        return null;
      });
  }
  return corePromise;
}
